import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { navIdToPath } from "@/types/auth";

const splashDuration = 3000; // ms

export default function RegistrationSuccessSplash() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get state from navigation
  const firstName = location.state?.firstName || location.state?.name || "";
  const inviterName = location.state?.inviterName || "";
  const isJoiningFamily = location.state?.isJoiningFamily || false;
  const trialEndDate = location.state?.trialEndDate || "";
  const isLogin = location.state?.isLogin || false;

  useEffect(() => {
    const timer = setTimeout(() => {
      // Check for active navigation item and use it for redirection
      const activeNav = localStorage.getItem('maatra-active-nav-item');
      const redirectPath = activeNav && navIdToPath[activeNav] ? navIdToPath[activeNav] : '/dashboard';
      navigate(redirectPath, { replace: true });
    }, splashDuration);
    return () => clearTimeout(timer);
  }, [navigate]);

  // Format trial end date if available
  const formatTrialEndDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Determine which message to show
  const isJoinFamily = isJoiningFamily && inviterName;
  const isCreateOwnAccount = !isJoinFamily && !isLogin;

  return (
    <div
      className="w-screen h-screen flex flex-col items-center justify-center bg-purple-170deg-gradient px-6"
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: "60vh" }}
      >
        {isLogin ? (
          // Message for login
          <>
            <h1
              className="font-bold animate-scale-in text-center mb-6"
              style={{ 
                fontSize: '48px',
                lineHeight: '1.2',
                color: '#36116D',
                transition: "all 0.8s cubic-bezier(0.4,0,0.2,1)"
              }}
            >
              Welcome back,<br />{firstName}
            </h1>
            <p 
              className="text-center animate-fade-in-delay" 
              style={{ 
                fontSize: '20px',
                fontWeight: 400,
                color: '#8764BB',
                maxWidth: '500px',
                lineHeight: '1.4'
              }}
            >
              You're logged in to Maatra
            </p>
          </>
        ) : isJoinFamily ? (
          // Message for users who joined a family
          <>
            <h1
              className="text-3xl md:text-4xl font-bold text-[#191919] mb-1 animate-scale-in text-center"
              style={{ transition: "all 0.8s cubic-bezier(0.4,0,0.2,1)" }}
            >
              Welcome to Maatra!
            </h1>
            {firstName && (
              <h2
                className="text-3xl md:text-4xl font-bold text-[#191919] mb-3 animate-scale-in text-center"
                style={{ transition: "all 0.8s cubic-bezier(0.4,0,0.2,1)" }}
              >
                {firstName}
              </h2>
            )}
            <p className="text-center max-w-xs animate-fade-in-delay" style={{ fontSize: 16, fontWeight: 600, color: '#606060' }}>
              You're all set. Let's take care of your family's health, together.
            </p>
          </>
        ) : (
          // Message for users who created their own account
          <>
            <h1
              className="font-bold animate-scale-in text-center mb-8"
              style={{ 
                fontSize: '48px',
                lineHeight: '1.2',
                color: '#36116D',
                transition: "all 0.8s cubic-bezier(0.4,0,0.2,1)"
              }}
            >
              You're all set,<br />{firstName}
            </h1>
            <p 
              className="text-center animate-fade-in-delay mb-16" 
              style={{ 
                fontSize: '20px',
                fontWeight: 400,
                color: '#8764BB',
                maxWidth: '500px',
                lineHeight: '1.4'
              }}
            >
              You're ready to start using Maatra for your family's health
            </p>
            {trialEndDate && (
              <p 
                className="text-center animate-fade-in-delay-2" 
                style={{ 
                  fontSize: '20px',
                  fontWeight: 400,
                  color: '#8764BB',
                  maxWidth: '500px',
                  lineHeight: '1.4'
                }}
              >
                Your free trial starts today and ends on <span style={{ color: '#36116D', fontWeight: 600 }}>{formatTrialEndDate(trialEndDate)}.</span>
              </p>
            )}
          </>
        )}
      </div>
      <div className="fixed left-0 right-0 bottom-0 h-1 bg-blue-400 animate-progress-bar" />
      <style>{`
        .animate-fade-in { opacity: 0; animation: fadeIn 0.9s forwards; }
        .animate-scale-in { transform: scale(0.9); opacity: 0; animation: scaleIn 1s 0.2s forwards; }
        .animate-fade-in-delay { opacity: 0; animation: fadeIn 1.2s 0.7s forwards; }
        .animate-fade-in-delay-2 { opacity: 0; animation: fadeIn 1.2s 1s forwards; }
        .animate-progress-bar { animation: progressBar ${splashDuration}ms linear forwards; }
        @keyframes fadeIn { to { opacity: 1; } }
        @keyframes scaleIn { to { transform: scale(1); opacity: 1; } }
        @keyframes progressBar { from { width: 0; } to { width: 100vw; } }
      `}</style>
    </div>
  );
}
